/**
 * Segmented Tab - per `Design/Mockups/_Notes - Login Modal.md`.
 *
 * Two-or-more segment tab strip. Selected segment: `card` bg + `foreground`
 * text. Unselected: `muted` bg (provided by container) + `muted-foreground`
 * text. 8px container radius, 4px gap. 4px container padding.
 */

export interface SegmentedTabItem {
  id: string;
  label: string;
}

export interface SegmentedTabProps {
  items: readonly SegmentedTabItem[];
  selected: string;
  onSelect: (id: string) => void;
}

export interface TabHandle {
  update: (props: SegmentedTabProps) => void;
  destroy: () => void;
}

interface ButtonRef {
  id: string;
  el: HTMLButtonElement;
  handler: () => void;
}

export function mountSegmentedTab(target: HTMLElement, props: SegmentedTabProps): TabHandle {
  const root = document.createElement('div');
  root.className = 'gv-segmented-tab';
  root.setAttribute('role', 'tablist');

  let buttons: ButtonRef[] = [];

  function render(p: SegmentedTabProps): void {
    // Tear down old buttons.
    for (const b of buttons) {
      b.el.removeEventListener('click', b.handler);
    }
    root.replaceChildren();
    buttons = [];

    for (const item of p.items) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gv-segmented-tab-item';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('data-id', item.id);
      btn.textContent = item.label;
      const isSelected = item.id === p.selected;
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      btn.tabIndex = isSelected ? 0 : -1;
      const handler = (): void => p.onSelect(item.id);
      btn.addEventListener('click', handler);
      buttons.push({ id: item.id, el: btn, handler });
      root.appendChild(btn);
    }
  }

  render(props);
  target.appendChild(root);

  return {
    update: (next: SegmentedTabProps): void => {
      render(next);
    },
    destroy: (): void => {
      for (const b of buttons) {
        b.el.removeEventListener('click', b.handler);
      }
      root.remove();
    },
  };
}
